import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { env, validateEnv } from '../config/env';
import { beginLogin, type IdentityProvider } from './oauth';
import { useAuth } from './useAuth';
import { Button } from '../components/ui/Button';
import styles from './LoginPage.module.css';

/**
 * Ops console login — Cognito hosted UI (authorization code + PKCE).
 *  - Primary: "Sign in with Google" (Google Workspace SAML IdP).
 *  - Break-glass: small "Admin login" link → hosted UI without forcing the
 *    IdP, so native Cognito admin users can still sign in.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  // Landing on "/" (Dashboard) is just the default route — send people to My Work instead.
  const returnTo =
    from?.pathname && from.pathname !== '/' ? `${from.pathname}${from.search ?? ''}` : undefined;
  const configProblems = validateEnv(env);

  if (status === 'signedIn') return <Navigate to={returnTo ?? '/work'} replace />;

  const start = async (identityProvider?: IdentityProvider) => {
    setError(null);
    setBusy(true);
    try {
      await beginLogin({ identityProvider, returnTo });
    } catch {
      setBusy(false);
      setError('We couldn’t start sign-in. Please try again.');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <img className={styles.logo} src="/dnm-wordmark-color.svg" alt={t('auth.loginPage.logoAlt')} />
          <div className={styles.title}>{t('auth.loginPage.title')}</div>
          <div className={styles.subtitle}>{t('auth.loginPage.subtitle')}</div>
        </div>

        <h2 className={styles.heading}>{t('auth.loginPage.heading')}</h2>
        <p className={styles.hint}>{t('auth.loginPage.hint')}</p>

        {configProblems.length > 0 && (
          <div className={styles.error} role="alert">
            {t('auth.loginPage.notConfigured', { problems: configProblems.join('; ') })}
          </div>
        )}
        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <Button
          variant="primary"
          className={styles.signIn}
          disabled={configProblems.length > 0}
          busy={busy}
          onClick={() => void start('GoogleWorkspace')}
        >
          {busy ? 'Redirecting…' : 'Sign in with Google'}
        </Button>
        <button
          type="button"
          className={styles.adminLink}
          disabled={busy || configProblems.length > 0}
          onClick={() => void start()}
        >
          {t('auth.loginPage.adminLogin')}
        </button>
      </div>
    </div>
  );
}
